import { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Star, Clock, MapPin, Video, UserCheck, ChevronRight, ChevronLeft,
  Check, Stethoscope, X, Building2, Award, Calendar, ChevronDown, Ban, Plus, CheckCircle, Shield, HelpCircle, GraduationCap, Briefcase, Mail, Loader2, ExternalLink
} from "lucide-react";
import { Link } from "wouter";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import MemberLayout from "@/layouts/MemberLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useListProviders, useListSpecialties, useListMasterCptCodes, useCreateCostEstimate, useInviteProviderToPortal, getListCostEstimatesQueryKey, useGetStates, useGetLocalities, API_BASE } from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY", "DC",
];

const POPULAR_LOCATIONS = ["New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX", "Phoenix, AZ"];
const STEP_LABELS = ["Filters & Codes", "State & Locality", "Providers"];

/* ─── MultiSelect Component ────────────────────────────────────────────────── */

function MultiSelect({
  options = [], value = [], onChange, placeholder, maxHeight = 260, localityMap = {},
}: {
  options: any[]; value: string[]; onChange: (v: string[]) => void;
  placeholder: string; maxHeight?: number; localityMap?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = useMemo(() => {
    return (options || []).map((o: any) => {
      if (typeof o === "string") return { value: o, label: o };
      if (typeof o === "object" && o !== null) {
        const val = String(o.value ?? o.id ?? o.localityId ?? o.code ?? o.name ?? "");
        const rawLbl = o.label ?? o.localityName ?? o.name ?? o.locality ?? o.description ?? o.value ?? o.id ?? "";
        const lbl = typeof rawLbl === "object" && rawLbl !== null
          ? String(rawLbl.name || rawLbl.localityName || rawLbl.code || rawLbl.id || "Option")
          : String(rawLbl || val || "Option");
        return { value: val, label: o.label ? String(o.label) : `${lbl}${o.state ? ` (${o.state})` : ""}` };
      }
      return { value: String(o ?? ""), label: String(o ?? "") };
    });
  }, [options]);

  const filtered = useMemo(() => {
    return normalizedOptions.filter(o =>
      String(o.label).toLowerCase().includes((search || "").toLowerCase())
    );
  }, [normalizedOptions, search]);

  const safeValue = Array.isArray(value) ? value : [];

  const toggle = (optValue: string) => {
    const strVal = String(optValue);
    onChange(safeValue.includes(strVal) ? safeValue.filter(v => v !== strVal) : [...safeValue, strVal]);
  };

  const selectAll = () => {
    onChange(filtered.map(o => o.value));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[48px] px-4 py-2 border border-slate-200 dark:border-slate-700/80 rounded-2xl bg-white dark:bg-slate-900 text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all shadow-sm"
        >
          {safeValue.length === 0 ? (
            <span className="text-slate-400 font-normal">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1.5 items-center flex-1 pr-2">
              {safeValue.slice(0, 3).map((v, idx) => {
                const foundOpt = normalizedOptions.find(o => o.value === v);
                let label = foundOpt?.label || localityMap?.[v];
                if (!label || label === v || v.length > 20) {
                  label = localityMap?.[v] || (foundOpt?.label && foundOpt.label !== v ? foundOpt.label : "Selected Area");
                }
                const itemKey = typeof v === 'string' ? v : `val-${idx}`;
                return (
                  <Badge key={itemKey} variant="secondary" className="text-xs rounded-lg px-2.5 py-1 bg-primary/10 text-primary border border-primary/20 font-medium">
                    {String(label)}
                    <span
                      className="ml-1.5 cursor-pointer hover:text-destructive text-primary/70 font-bold"
                      onClick={e => { e.stopPropagation(); toggle(v); }}
                    >&times;</span>
                  </Badge>
                );
              })}
              {safeValue.length > 3 && (
                <Badge variant="outline" className="text-xs rounded-lg px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 font-semibold">
                  +{safeValue.length - 3} more
                </Badge>
              )}
            </div>
          )}
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden" align="start">
        <div className="p-2.5 border-b border-slate-100 dark:border-slate-800 space-y-2">
          <Input
            placeholder="Search localities..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-9 text-xs rounded-xl border-slate-200 dark:border-slate-700"
            autoFocus
          />
          <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500 px-1 pt-0.5">
            <span>{safeValue.length} selected</span>
            <div className="flex gap-2">
              <button type="button" onClick={selectAll} className="text-primary hover:underline">Select All</button>
              <span className="text-slate-300">|</span>
              <button type="button" onClick={clearAll} className="text-slate-400 hover:text-slate-600">Clear</button>
            </div>
          </div>
        </div>
        <div className="overflow-auto p-1.5" style={{ maxHeight }}>
          {filtered.length === 0 ? (
            <div className="p-4 text-xs text-center text-slate-400">No matching localities</div>
          ) : (
            filtered.map(o => {
              const isSelected = safeValue.includes(o.value);
              return (
                <div
                  key={o.value}
                  onClick={() => toggle(o.value)}
                  className={cn(
                    "px-3 py-2 text-xs rounded-xl cursor-pointer flex items-center justify-between transition-all my-0.5 font-medium",
                    isSelected ? "bg-primary/10 text-primary font-semibold" : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
                  )}
                >
                  <span className="truncate pr-2">{o.label}</span>
                  {isSelected && <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
const AVATAR_GRADIENTS = [
  "from-blue-500 to-indigo-600",
  "from-violet-500 to-purple-600",
  "from-cyan-500 to-blue-500",
  "from-emerald-500 to-teal-600",
  "from-rose-500 to-pink-600",
  "from-amber-500 to-orange-500",
  "from-fuchsia-500 to-pink-500",
  "from-teal-500 to-emerald-500",
];

const ALL_SLOTS = [
  "8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
  "11:00 AM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM",
  "3:30 PM", "4:00 PM", "4:30 PM",
];

type Step = 0 | 1 | 2;
type SortKey = "rating" | "experience" | "reviews";

// ─── Helpers ──────────────────────────────────────────────────────────────────

type SlotStatus = "available" | "booked";
interface SlotInfo { time: string; status: SlotStatus }

/** Returns all slots for a day with their status, or [] if day is closed */
function getDaySlots(providerId: string | number, dayOffset: number): SlotInfo[] {
  let numId = typeof providerId === "number" ? providerId : 0;
  if (typeof providerId === "string") {
    for (let i = 0; i < providerId.length; i++) {
      numId += providerId.charCodeAt(i);
    }
  }

  // ~1 in 5 chance the provider has no office hours that day (closed)
  const isClosed = ((numId * 7 + dayOffset * 3) % 5) === 0;
  if (isClosed) return [];
  const seed = (numId * 31 + dayOffset * 17 + numId) % 97;
  return ALL_SLOTS.map((time, i) => ({
    time,
    status: ((seed + i * 13) % 5) < 3 ? "available" : "booked",
  }));
}

function getWeekDays(): { label: string; short: string; date: Date; offset: number }[] {
  const today = new Date();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return {
      label: d.toLocaleDateString("en-US", { weekday: "short" }),
      short: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      date: d,
      offset: i,
    };
  });
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-3 h-3 ${i < Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-slate-200 dark:text-slate-700"}`} />
      ))}
    </div>
  );
}

// ─── Availability Calendar Widget ─────────────────────────────────────────────

function AvailabilityCalendar({ providerId, providerName }: { providerId: string | number; providerName: string }) {
  const weekDays = useMemo(getWeekDays, []);
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [confirmedSlot, setConfirmedSlot] = useState<string | null>(null);

  const allSlots = useMemo(() => getDaySlots(providerId, selectedDay), [providerId, selectedDay]);
  const isClosed = allSlots.length === 0;
  const availableCount = allSlots.filter(s => s.status === "available").length;
  const isFullyBooked = !isClosed && availableCount === 0;

  return (
    <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-4">
      {/* Header + legend */}
      <div className="flex flex-wrap items-center justify-between gap-y-2 mb-3">
        <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5" /> Schedule — Next 7 Days
        </p>
        <div className="flex items-center gap-3 text-[10px] text-slate-400 font-medium">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" />Available</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-100 dark:bg-slate-800 border border-dashed border-slate-300 inline-block" />Closed/Full</span>
        </div>
      </div>

      {/* Day strip */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {weekDays.map((day, i) => {
          const dayInfo = getDaySlots(providerId, i);
          const isDayClosed = dayInfo.length === 0;
          const avail = dayInfo.filter(s => s.status === "available").length;
          const isFullyBooked = !isDayClosed && avail === 0;
          const isSelected = selectedDay === i;

          return (
            <button
              key={i}
              onClick={() => { setSelectedDay(i); setSelectedSlot(null); }}
              className={`flex flex-col items-center py-2 px-0.5 rounded-xl text-center transition-all ${isDayClosed
                ? "bg-slate-50 dark:bg-slate-800/40 text-slate-300 dark:text-slate-700 cursor-pointer border border-dashed border-slate-200 dark:border-slate-700"
                : isSelected && isFullyBooked
                  ? "bg-slate-100 dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400"
                  : isSelected
                    ? "bg-primary text-white shadow-md shadow-primary/20"
                    : isFullyBooked
                      ? "bg-slate-50 dark:bg-slate-900/50 text-slate-400 dark:text-slate-500 border border-slate-100 dark:border-slate-800 hover:border-slate-200"
                      : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-primary/10 hover:text-primary border border-slate-100 dark:border-slate-700"
                }`}
            >
              <span className="text-[9px] font-bold uppercase tracking-wider leading-tight">{day.label}</span>
              <span className={`text-xs font-bold mt-0.5 leading-tight ${isSelected && !isFullyBooked ? "text-white" : ""}`}>
                {day.date.getDate()}
              </span>
              <span className={`text-[8px] mt-0.5 font-semibold leading-tight ${isDayClosed ? "text-slate-300 dark:text-slate-700" :
                isSelected && !isFullyBooked ? "text-white/80" :
                  isFullyBooked ? "text-slate-400 dark:text-slate-500" :
                    "text-emerald-500"
                }`}>
                {isDayClosed ? "—" : isFullyBooked ? "Full" : `${avail} open`}
              </span>
            </button>
          );
        })}
      </div>

      {/* Slot grid */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`slots-${selectedDay}`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {isClosed ? (
            <div className="flex items-center justify-center py-3 gap-2 text-slate-400">
              <Ban className="w-4 h-4 opacity-40" />
              <p className="text-sm font-semibold text-slate-500">Closed on this date</p>
            </div>
          ) : isFullyBooked ? (
            <div className="flex items-center justify-center py-3 gap-2 text-slate-400">
              <Calendar className="w-4 h-4 opacity-40" />
              <p className="text-sm font-semibold text-slate-500">Fully booked on this date</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5 sm:gap-2">
                {allSlots.filter(s => s.status === 'available').map(({ time }) => {
                  const isSelected = selectedSlot === time;
                  const isConfirmed = confirmedSlot === time;
                  return (
                    <button
                      key={time}
                      onClick={() => setSelectedSlot(isSelected ? null : time)}
                      className={`relative text-xs font-semibold py-2 px-1 rounded-xl border transition-all ${isConfirmed
                        ? "bg-emerald-500 text-white border-emerald-500 shadow-sm"
                        : isSelected
                          ? "bg-primary text-white border-primary shadow-sm shadow-primary/20"
                          : "bg-white dark:bg-slate-900 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 hover:border-emerald-400 dark:hover:bg-emerald-900/20 shadow-sm"
                        }`}
                    >
                      {isConfirmed ? (
                        <span className="flex items-center justify-center gap-0.5">
                          <Check className="w-2.5 h-2.5" /> Done
                        </span>
                      ) : time}
                    </button>
                  );
                })}
              </div>

              {/* Available count summary */}
              <p className="text-[10px] text-slate-400 mt-3 text-center">
                <span className="text-emerald-600 font-semibold">{availableCount} appointments available</span> on {weekDays[selectedDay].label}
              </p>

              {/* Booking confirmation bar */}
              <AnimatePresence>
                {selectedSlot && confirmedSlot !== selectedSlot && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {weekDays[selectedDay].short} · {selectedSlot}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">with {providerName}</p>
                      </div>
                      <Link href={`/provider/${(providerName || 'provider').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}/${providerId}`}>
                        <Button size="sm" className="rounded-full h-8 px-4 text-xs font-bold bg-primary text-white border-0 shadow-sm shadow-primary/20 flex-shrink-0">
                          Confirm Booking
                        </Button>
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ProviderSearch() {
  const [step, setStep] = useState<Step>(0);
  const [selectedState, setSelectedState] = useState("");
  const [selectedLocalityIds, setSelectedLocalityIds] = useState<string[]>([]);
  const [location, setLocation] = useState("");
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [gender, setGender] = useState("");
  const [visitType, setVisitType] = useState("");
  const [practiceType, setPracticeType] = useState("");
  const [sort, setSort] = useState<SortKey>("rating");
  const [expandedCalendar, setExpandedCalendar] = useState<number | null>(null);

  const { data: statesData = [] } = useGetStates();
  const US_STATES = useMemo(() => {
    if (!statesData || !Array.isArray(statesData) || statesData.length === 0) return DEFAULT_US_STATES;
    return statesData.map((s: any) => typeof s === 'string' ? s : String(s?.code || s?.name || s?.id || '')).filter(Boolean);
  }, [statesData]);
  const localityCacheRef = useRef<Record<string, string>>({});
  const { data: localitiesData = [] } = useGetLocalities(selectedState);
  useEffect(() => {
    if (Array.isArray(localitiesData)) {
      localitiesData.forEach((loc: any) => {
        if (typeof loc === 'object' && loc !== null) {
          const id = String(loc.id ?? loc.localityId ?? loc.value ?? "");
          const name = String(loc.name ?? loc.localityName ?? loc.label ?? loc.description ?? id);
          const stateStr = loc.state ? ` (${loc.state}${loc.city ? ' - ' + loc.city : ''})` : "";
          if (id) localityCacheRef.current[id] = `${name}${stateStr}`;
        }
      });
    }
  }, [localitiesData]);

  const LOCALITIES = localitiesData.map((loc: any) => ({
    value: loc.id.toString(),
    label: `${loc.name} (${loc.state}${loc.city ? ' - ' + loc.city : ''})`
  }));

  const [providerSearch, setProviderSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [specOpen, setSpecOpen] = useState(false);
  const [specSearch, setSpecSearch] = useState("");
  const { data: specialties } = useListSpecialties();

  const [compareList, setCompareList] = useState<any[]>([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [selectedLinkedProviders, setSelectedLinkedProviders] = useState<Record<string, number>>({});

  const [estimatorProvider, setEstimatorProvider] = useState<any>(null);
  const [inviteProviderTarget, setInviteProviderTarget] = useState<any>(null);
  const [invitePatientName, setInvitePatientName] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const inviteProviderMutation = useInviteProviderToPortal();

  const handleSendInvitation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteProviderTarget?.id) return;

    inviteProviderMutation.mutate({
      id: inviteProviderTarget.id,
      patientName: invitePatientName.trim() || undefined,
      message: inviteNote.trim() || undefined
    }, {
      onSuccess: () => {
        toast({
          title: "Invitation Sent!",
          description: `We've sent an invitation to ${inviteProviderTarget.name || 'the provider'} to join StraightDoc.`
        });
        setInviteProviderTarget(null);
        setInvitePatientName("");
        setInviteNote("");
      },
      onError: (err: any) => {
        toast({
          variant: "destructive",
          title: "Invitation Failed",
          description: err.message || "Could not send invitation. Please try again."
        });
      }
    });
  };

  const [selectedCptCodes, setSelectedCptCodes] = useState<any[]>([]);
  const [cptQuantities, setCptQuantities] = useState<Record<string, number>>({});
  const [cptSearchQuery, setCptSearchQuery] = useState("");

  const { data: masterCptCodes } = useListMasterCptCodes();
  const createEstimate = useCreateCostEstimate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const activeSpecialties = useMemo(() =>
    (specialties ?? []).filter((s: any) =>
      !specSearch || s.name.toLowerCase().includes(specSearch.toLowerCase())
    ), [specialties, specSearch]);

  const params: Record<string, any> = {};
  if (selectedState) params.state = selectedState;
  if (selectedLocalityIds.length > 0) params.localityIds = selectedLocalityIds.join(",");
  if (location) params.location = location;
  if (selectedSpecialties.length > 0) params.specialty = selectedSpecialties.join(",");
  if (gender) params.gender = gender;
  if (visitType) params.visitType = visitType;
  if (practiceType) params.practiceType = practiceType;

  if (providerSearch) params.search = providerSearch;
  params.page = currentPage;
  params.limit = itemsPerPage;

  const { data: providersData, isLoading } = useListProviders(
    step === 2 ? params : undefined,
    { query: { enabled: step === 2, queryKey: ["providers", params, step] } }
  );

  const providers = providersData?.items || [];
  const totalEntries = providersData?.totalCount || 0;
  const totalPages = providersData?.totalPages || 1;

  const sorted = useMemo(() =>
    [...(providers ?? [])]
      .sort((a, b) => {
        if (sort === "experience") return b.yearsExperience - a.yearsExperience;
        return b.rating - a.rating;
      }),
    [providers, sort]
  );

  const startIndex = totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalEntries);
  const paginatedProviders = sorted;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  const resetFlow = () => {
    setStep(0); setSelectedState(""); setSelectedLocalityIds([]); setLocation(""); setSelectedSpecialties([]); setGender("");
    setVisitType(""); setPracticeType(""); setExpandedCalendar(null);
    setProviderSearch(""); setCurrentPage(1);
  };

  const activeFilterCount = [selectedState, selectedLocalityIds.length > 0, selectedSpecialties.length > 0, gender, visitType, practiceType].filter(Boolean).length;

  return (
    <MemberLayout>
      <div className="w-full space-y-6 sm:space-y-8 pt-2 sm:pt-4 pb-16 sm:pb-24">
        {/* Header */}
        <div className="flex items-center gap-3.5 sm:gap-4">
          <div className="p-2.5 sm:p-3 bg-primary/10 rounded-xl flex-shrink-0 text-primary">
            <Search className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Find Care</h1>
            <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-0.5">Search for providers accepting patients in your state & locality area</p>
          </div>
        </div>

        {/* Wizard card */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl shadow-sm border border-slate-200/80 dark:border-slate-800 overflow-hidden transition-all">

          {/* Step tabs */}
          <div className="p-2.5 sm:p-3 bg-slate-50/80 dark:bg-slate-800/40 border-b border-slate-200/60 dark:border-slate-800">
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2 bg-slate-200/60 dark:bg-slate-800/80 p-1 sm:p-1.5 rounded-xl sm:rounded-2xl">
              {STEP_LABELS.map((label, i) => {
                const done = step > i;
                const active = step === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => (done ? setStep(i as Step) : undefined)}
                    className={cn(
                      "relative flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3 px-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200",
                      active
                        ? "bg-white dark:bg-slate-900 text-primary shadow-sm border border-slate-200/50 dark:border-slate-700/50 font-bold"
                        : done
                        ? "text-slate-700 dark:text-slate-300 hover:bg-white/50 dark:hover:bg-slate-800/50 cursor-pointer"
                        : "text-slate-400 dark:text-slate-500 cursor-default"
                    )}
                  >
                    <span
                      className={cn(
                        "w-5 h-5 sm:w-6 sm:h-6 rounded-md sm:rounded-lg flex items-center justify-center text-[11px] sm:text-xs font-bold flex-shrink-0 transition-transform",
                        active
                          ? "bg-primary text-white shadow-md shadow-primary/30 scale-105"
                          : done
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
                      )}
                    >
                      {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <span className="hidden sm:inline truncate">
                      {active ? `Step ${i + 1}: ${label}` : label}
                    </span>
                    {done && i === 1 && activeFilterCount > 0 && (
                      <span className="hidden sm:flex w-4 h-4 bg-primary text-white rounded-full text-[9px] font-bold items-center justify-center ml-0.5">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <AnimatePresence mode="wait">

            {/* ── STEP 1: Filters & Codes ── */}
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.18 }} className="p-4 sm:p-6 lg:p-8 space-y-6">
                <div className="flex items-start gap-3.5 sm:gap-4 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-blue-50 via-indigo-50/40 to-transparent dark:from-slate-800/60 dark:via-slate-800/30 dark:to-transparent border border-blue-100/80 dark:border-slate-800">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary">
                    <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-900 dark:text-white text-lg sm:text-xl">Search Codes & Refine Filters</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-0.5">Select code types, procedure codes, or specialties</p>
                  </div>
                </div>

                {/* Specialty */}
                <div className="relative">
                  <p className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-2">Specialty</p>

                  {/* Selected Pills */}
                  {selectedSpecialties.length > 0 && !specOpen && (
                    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap gap-2 mb-3">
                      {selectedSpecialties.map(spec => (
                        <span key={spec} className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/20 text-primary rounded-xl px-3 py-1.5 text-sm font-semibold">
                          {spec}
                          <button onClick={() => setSelectedSpecialties(s => s.filter(x => x !== spec))} className="hover:text-primary/70 transition-colors">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                      <button onClick={() => setSelectedSpecialties([])} className="text-xs text-slate-400 hover:text-slate-600 font-medium px-2 py-1.5">
                        Clear all
                      </button>
                    </motion.div>
                  )}

                  {/* Search Input using Popover to avoid clipping */}
                  <Popover open={specOpen} onOpenChange={setSpecOpen}>
                    <PopoverAnchor asChild>
                      <div className={`relative flex items-center rounded-2xl border transition-all cursor-text ${specOpen ? "border-primary ring-2 ring-primary/15 bg-white dark:bg-slate-900" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-primary/40"
                        }`} onClick={() => setSpecOpen(true)}>
                        <Search className={`absolute left-4 w-4 h-4 flex-shrink-0 transition-colors ${specOpen ? "text-primary" : "text-slate-400"}`} />
                        <input
                          value={specSearch}
                          onChange={e => { setSpecSearch(e.target.value); setSpecOpen(true); }}
                          onFocus={() => setSpecOpen(true)}
                          placeholder={selectedSpecialties.length > 0 && !specOpen ? "Add another specialty…" : "Search specialties (e.g. Cardiologist)…"}
                          className="w-full h-12 pl-11 pr-4 bg-transparent text-sm text-slate-700 dark:text-slate-300 placeholder:text-slate-400 outline-none rounded-2xl"
                        />
                        {specSearch && (
                          <button onClick={(e) => { e.stopPropagation(); setSpecSearch(""); }} className="absolute right-4 text-slate-400 hover:text-slate-600 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </PopoverAnchor>
                    <PopoverContent
                      className="w-[var(--radix-popover-trigger-width)] p-0 rounded-2xl shadow-2xl border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 z-[100]"
                      align="start"
                      sideOffset={8}
                    >
                      <div className="max-h-60 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-800/60">
                        {activeSpecialties.length === 0 ? (
                          <div className="py-8 text-center text-sm text-slate-400">
                            <Search className="w-6 h-6 mx-auto mb-2 opacity-20" />
                            No specialties found
                          </div>
                        ) : activeSpecialties.map(s => {
                          const isSelected = selectedSpecialties.includes(s.name);
                          return (
                            <button
                              key={s.id}
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => {
                                setSelectedSpecialties(prev => isSelected ? prev.filter(x => x !== s.name) : [...prev, s.name]);
                                setSpecSearch("");
                              }}
                              className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition-colors group ${isSelected ? "bg-primary/5" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                }`}
                            >
                              <span className={`text-sm font-medium ${isSelected ? "text-primary" : "text-slate-700 dark:text-slate-300"}`}>
                                {s.name}
                              </span>
                              <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isSelected ? "bg-primary border-primary text-white" : "border-slate-300 dark:border-slate-600 group-hover:border-primary/50"
                                }`}>
                                {isSelected && <Check className="w-3.5 h-3.5" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 flex justify-end">
                        <Button size="sm" onClick={() => setSpecOpen(false)} className="rounded-xl h-8 px-4 text-xs font-bold">
                          Done
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                  {/* Gender */}
                  <div className="bg-slate-50/60 dark:bg-slate-800/30 rounded-2xl p-4 border border-slate-200/60 dark:border-slate-800 flex flex-col justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-3">Provider Gender</p>
                      <div className="flex gap-2 flex-wrap">
                        {["Any", "Male", "Female", "Non-binary"].map(g => (
                          <button key={g}
                            type="button"
                            onClick={() => setGender(g === "Any" ? "" : g === gender ? "" : g)}
                            className={`px-3 py-1.5 sm:py-2 rounded-xl border text-xs sm:text-sm font-medium transition-all ${(g === "Any" && gender === "") || gender === g
                              ? "bg-primary text-white border-primary shadow-sm"
                              : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-primary/40 hover:text-primary bg-white dark:bg-slate-900"
                              }`}
                          >{g}</button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Visit Type */}
                  <div className="bg-slate-50/60 dark:bg-slate-800/30 rounded-2xl p-4 border border-slate-200/60 dark:border-slate-800 flex flex-col justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-3">Visit Type</p>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { label: "Any", val: "" },
                          { label: "Video", val: "Telehealth" },
                          { label: "In-person", val: "In-person" }
                        ].map(v => (
                          <button key={v.label}
                            type="button"
                            onClick={() => setVisitType(v.val)}
                            className={`px-3 py-1.5 sm:py-2 rounded-xl border text-xs sm:text-sm font-medium transition-all ${visitType === v.val
                              ? "bg-primary text-white border-primary shadow-sm"
                              : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-primary/40 hover:text-primary bg-white dark:bg-slate-900"
                              }`}
                          >{v.label}</button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Facility Type */}
                  <div className="bg-slate-50/60 dark:bg-slate-800/30 rounded-2xl p-4 border border-slate-200/60 dark:border-slate-800 flex flex-col justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-3">Facility Type</p>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { label: "Any", val: "" },
                          { label: "Group Practice", val: "Group Practice" },
                          { label: "Urgent Care", val: "Urgent Care" },
                          { label: "Clinic", val: "Clinic" },
                          { label: "Hospital", val: "Hospital" },
                          { label: "Multi Speciality", val: "Multi Speciality" },
                        ].map(p => (
                          <button key={p.label}
                            type="button"
                            onClick={() => setPracticeType(p.val)}
                            className={`px-3 py-1.5 sm:py-2 rounded-xl border text-xs sm:text-sm font-medium transition-all ${practiceType === p.val
                              ? "bg-primary text-white border-primary shadow-sm"
                              : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-primary/40 hover:text-primary bg-white dark:bg-slate-900"
                              }`}
                          >{p.label}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Button
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0 font-bold text-base gap-2 shadow-lg shadow-blue-500/25 active:scale-[0.99] transition-all"
                    data-testid="button-specialty-continue"
                  >
                    Continue to State & Locality <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── STEP 2: Location & Locality ── */}
            {step === 1 && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.2 }}
                className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-7"
              >
                {/* Header Banner inside Step 2 */}
                <div className="flex items-center gap-3.5 sm:gap-4 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-blue-50 via-indigo-50/50 to-transparent dark:from-slate-800/60 dark:via-slate-800/30 dark:to-transparent border border-blue-100/80 dark:border-slate-800">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-primary text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/25">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-900 dark:text-white text-lg sm:text-xl">
                      Select Location & Primary Locality Area
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-0.5">
                      Select your state and locality area to find care providers accepting patients near you
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                  {/* State Select */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider block">
                      State <span className="text-primary">*</span>
                    </label>
                    <Select
                      value={selectedState}
                      onValueChange={(val) => {
                        setSelectedState(val);
                        setSelectedLocalityIds([]);
                      }}
                    >
                      <SelectTrigger className="h-12 text-sm rounded-2xl border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary/20 shadow-sm">
                        <SelectValue placeholder="Select State (e.g. CA, NY, TX)" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 z-50 rounded-2xl shadow-2xl">
                        {US_STATES.map((s: string) => (
                          <SelectItem key={s} value={s} className="rounded-xl my-0.5">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Primary Locality MultiSelect */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider block">
                        Primary Locality / Area
                      </label>
                      {selectedState && (
                        <span className="text-[11px] text-primary font-semibold px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                          Filtered by {selectedState}
                        </span>
                      )}
                    </div>
                    <MultiSelect
                      options={LOCALITIES}
                      value={selectedLocalityIds}
                      onChange={setSelectedLocalityIds}
                      localityMap={localityCacheRef.current}
                      placeholder={
                        selectedState
                          ? `Select localities in ${selectedState}...`
                          : "Select state first to view localities..."
                      }
                    />
                  </div>

                  {/* City/Address/Zip Code (Full Width) */}
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider block">
                      City, Address or ZIP Code <span className="text-slate-400 font-normal text-normal lowercase">(Optional)</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && setStep(2)}
                        placeholder="e.g. Los Angeles, 90210, Main St"
                        className="pl-11 pr-10 h-12 text-sm rounded-2xl border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary/20 shadow-sm"
                        data-testid="input-location"
                      />
                      {location && (
                        <button
                          type="button"
                          onClick={() => setLocation("")}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Continue Button */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep(0)}
                    className="rounded-2xl gap-1.5 border-slate-200 dark:border-slate-700 h-12 px-6 font-semibold"
                  >
                    <ChevronLeft className="w-4 h-4" /> Back to Filters
                  </Button>
                  <Button
                    onClick={() => setStep(2)}
                    disabled={
                      !selectedState && selectedLocalityIds.length === 0 && !location.trim()
                    }
                    className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0 font-bold text-base gap-2 shadow-lg shadow-blue-500/25 active:scale-[0.99] transition-all disabled:opacity-50 disabled:shadow-none"
                    data-testid="button-location-continue"
                  >
                    Find Providers <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── STEP 3: Providers ── */}
            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.18 }} className="min-h-0">
                <div className="flex flex-col xl:grid xl:grid-cols-[280px_1fr] xl:divide-x xl:divide-slate-200/60 dark:xl:divide-slate-800 border-t border-slate-200/60 dark:border-slate-800">

                  {/* ── LEFT: search summary & filters ── */}
                  <div className="p-4 sm:p-5 lg:p-6 space-y-4 bg-slate-50/50 dark:bg-slate-900/50 border-b xl:border-b-0 xl:border-r border-slate-200/60 dark:border-slate-800 shrink-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-1 gap-4">
                      {/* Search Summary Card */}
                      <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-200/70 dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-2 mb-4 sm:mb-5">
                          <Search className="w-4 h-4 text-slate-400" />
                          <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5">Your Search</p>
                        </div>

                        <div className="space-y-3.5">
                          <div className="flex items-center gap-3 sm:gap-4">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#f0f5ff] dark:bg-blue-900/30 flex items-center justify-center text-[#4F46E5] dark:text-blue-400 border border-[#e0ebff] dark:border-blue-800/50 flex-shrink-0">
                              <MapPin className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="text-sm sm:text-[15px] font-bold text-slate-800 dark:text-slate-200 block truncate">
                                {selectedState ? `State: ${selectedState}` : (location || "All Locations")}
                              </span>
                              {selectedLocalityIds.length > 0 && (
                                <span className="text-xs text-primary font-semibold block truncate">
                                  {selectedLocalityIds.length} Locality Area{selectedLocalityIds.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>

                          {selectedSpecialties.length > 0 && (
                            <div className="flex items-center gap-3 sm:gap-4">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50 flex-shrink-0">
                                <Stethoscope className="w-4 h-4" />
                              </div>
                              <div className="text-sm sm:text-[15px] font-bold text-slate-800 dark:text-slate-200 leading-tight min-w-0 flex-1">
                                <span className="block truncate">{selectedSpecialties.slice(0, 2).join(", ")}</span>
                                {selectedSpecialties.length > 2 && (
                                  <span className="block text-xs text-indigo-500 font-bold mt-0.5">
                                    +{selectedSpecialties.length - 2} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {gender && (
                            <div className="flex items-center gap-3 sm:gap-4">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-violet-50 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400 border border-violet-100 dark:border-violet-800/50 flex-shrink-0">
                                <UserCheck className="w-4 h-4" />
                              </div>
                              <span className="text-sm sm:text-[15px] font-bold text-slate-800 dark:text-slate-200 truncate">{gender} Providers</span>
                            </div>
                          )}

                          {visitType === "Telehealth" && (
                            <div className="flex items-center gap-3 sm:gap-4">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-600 dark:text-cyan-400 border border-cyan-100 dark:border-cyan-800/50 flex-shrink-0">
                                <Video className="w-4 h-4" />
                              </div>
                              <span className="text-sm sm:text-[15px] font-bold text-slate-800 dark:text-slate-200 truncate">Telehealth Only</span>
                            </div>
                          )}

                          {visitType === "In-person" && (
                            <div className="flex items-center gap-3 sm:gap-4">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800/50 flex-shrink-0">
                                <Building2 className="w-4 h-4" />
                              </div>
                              <span className="text-sm sm:text-[15px] font-bold text-slate-800 dark:text-slate-200 truncate">In-person Only</span>
                            </div>
                          )}

                          {practiceType && (
                            <div className="flex items-center gap-3 sm:gap-4">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 border border-orange-100 dark:border-orange-800/50 flex-shrink-0">
                                <Building2 className="w-4 h-4" />
                              </div>
                              <span className="text-sm sm:text-[15px] font-bold text-slate-800 dark:text-slate-200 truncate">{practiceType} Practice</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Sort By Card */}
                      <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-200/70 dark:border-slate-800 shadow-sm flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between mb-4 sm:mb-5">
                            <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Sort By</p>
                            <span className="text-[11px] font-bold text-[#4F46E5] dark:text-blue-400 bg-[#f0f5ff] dark:bg-blue-900/30 px-2.5 py-1 rounded-full uppercase tracking-wider">
                              {isLoading ? "..." : `${sorted.length} found`}
                            </span>
                          </div>

                          <div className="space-y-2">
                            <button onClick={() => setSort("rating")}
                              className={`w-full text-left text-sm sm:text-[15px] px-3.5 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl font-bold transition-all flex items-center justify-between group ${sort === "rating"
                                ? "bg-primary text-white shadow-md shadow-primary/20"
                                : "bg-slate-50 dark:bg-slate-800/70 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200/60 dark:border-slate-700/60"
                                }`}
                            >
                              <span className="flex items-center gap-2.5 min-w-0">
                                <Star className={`w-4 h-4 shrink-0 ${sort === "rating" ? "text-white" : "text-amber-500"}`} />
                                <span className="leading-tight mt-0.5 whitespace-nowrap">Top Rated</span>
                              </span>
                              {sort === "rating" && <Check className="w-4 h-4 shrink-0 ml-1.5" />}
                            </button>

                            <button onClick={() => setSort("experience")}
                              className={`w-full text-left text-sm sm:text-[15px] px-3.5 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl font-bold transition-all flex items-center justify-between group ${sort === "experience"
                                ? "bg-primary text-white shadow-md shadow-primary/20"
                                : "bg-slate-50 dark:bg-slate-800/70 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200/60 dark:border-slate-700/60"
                                }`}
                            >
                              <span className="flex items-center gap-2.5 min-w-0">
                                <Briefcase className={`w-4 h-4 shrink-0 ${sort === "experience" ? "text-white" : "text-emerald-500"}`} />
                                <span className="leading-tight mt-0.5 whitespace-nowrap">Most Experience</span>
                              </span>
                              {sort === "experience" && <Check className="w-4 h-4 shrink-0 ml-1.5" />}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="pt-1 pb-2 space-y-2.5 md:col-span-2 lg:col-span-1">
                        <Button variant="outline" onClick={() => setStep(1)}
                          className="w-full rounded-xl sm:rounded-full border-slate-200 dark:border-slate-700 h-11 sm:h-12 text-sm sm:text-[15px] font-bold bg-white dark:bg-slate-900 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-all text-slate-700 dark:text-slate-200"
                        >
                          <ChevronLeft className="w-4 h-4 mr-1.5" /> Edit Filters
                        </Button>
                        <button onClick={resetFlow}
                          className="w-full text-center text-[11px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 py-1.5 flex items-center justify-center gap-1.5 transition-colors font-bold uppercase tracking-wider"
                        >
                          <X className="w-3.5 h-3.5" /> Start new search
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ── RIGHT: Provider cards ── */}
                  <div className="p-4 sm:p-6 lg:p-7 flex flex-col space-y-5 bg-slate-50/30 dark:bg-slate-950/30 flex-1 min-w-0">

                    {/* Search bar inside right column */}
                    {!isLoading && (
                      <div className="relative shrink-0">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={providerSearch}
                          onChange={(e) => { setProviderSearch(e.target.value); setCurrentPage(1); }}
                          placeholder="Search providers by name..."
                          className="pl-10 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 h-11 shadow-sm rounded-xl focus-visible:ring-primary/30 text-sm"
                        />
                      </div>
                    )}

                    {isLoading ? Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="rounded-2xl border border-slate-200/70 dark:border-slate-800 p-5 shrink-0 bg-white dark:bg-slate-900">
                        <div className="flex gap-4">
                          <Skeleton className="w-14 h-14 rounded-2xl flex-shrink-0" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-5 w-44" />
                            <Skeleton className="h-4 w-28" />
                            <Skeleton className="h-4 w-36" />
                            <Skeleton className="h-9 w-36 rounded-full mt-1" />
                          </div>
                        </div>
                      </div>
                    )) : (providers ?? []).length === 0 ? (
                      <div className="flex flex-col items-center justify-center text-center p-12 sm:p-16 bg-white dark:bg-slate-900/30 border border-slate-200/70 dark:border-slate-800 rounded-3xl mt-2 shrink-0">
                        <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 shadow-sm rounded-full flex items-center justify-center mb-6 border border-slate-100 dark:border-slate-700">
                          <Stethoscope className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No providers found</h3>
                        <p className="text-slate-500 max-w-sm mb-8 text-sm leading-relaxed">We couldn't find any providers matching your exact search criteria. Try removing some filters or expanding your search area.</p>
                        <Button onClick={() => setStep(1)} className="rounded-full px-8 shadow-sm font-semibold">
                          Adjust Filters
                        </Button>
                      </div>
                    ) : totalEntries === 0 ? (
                      <div className="flex flex-col items-center justify-center text-center p-12 sm:p-16 bg-white dark:bg-slate-900/30 border border-slate-200/70 dark:border-slate-800 rounded-3xl mt-2 shrink-0">
                        <Search className="w-10 h-10 text-slate-300 dark:text-slate-600 mb-6 opacity-40" />
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No providers match your search</h3>
                        <p className="text-slate-500 max-w-sm mb-8 text-sm leading-relaxed">Try using different keywords or clearing your search.</p>
                        <Button onClick={() => { setProviderSearch(""); setCurrentPage(1); }} variant="outline" className="rounded-full px-8 shadow-sm font-semibold">
                          Clear Search
                        </Button>
                      </div>
                    ) : paginatedProviders.map((p, i) => {
                      const gradient = AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length];
                      const linkedProviders = p.linkedProviders || [];
                      const defaultSelectedId = linkedProviders.length > 0 ? linkedProviders[0].id : null;
                      const selectedLinkedProviderId = selectedLinkedProviders[p.id] || defaultSelectedId;
                      const selectedLinkedProvider = linkedProviders.find((lp: any) => lp.id === selectedLinkedProviderId) || (linkedProviders.length > 0 ? linkedProviders[0] : null);
                      const initials = p.name.split(" ").map((n: string) => n[0]).slice(0, 2).join("");
                      const isBest = i === 0;
                      const calOpen = expandedCalendar === p.id;
                      const primaryAddress = p.addresses?.find((a: any) => a.isPrimary) || p.addresses?.[0];
                      const addressString = primaryAddress
                        ? `${primaryAddress.street1}${primaryAddress.street2 ? ' ' + primaryAddress.street2 : ''}, ${primaryAddress.city}, ${primaryAddress.state} ${primaryAddress.zipCode}`
                        : "Address not provided";

                      return (
                        <motion.div
                          key={p.id}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.05, duration: 0.2 }}
                          className={`shrink-0 border ${p.practiceType?.toLowerCase() === 'group' || p.practiceType?.toLowerCase() === 'clinic'
                            ? 'border-indigo-200 dark:border-indigo-800/50 bg-gradient-to-br from-indigo-50/50 to-white dark:from-indigo-900/10 dark:to-slate-900'
                            : 'border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900'
                            } rounded-2xl mb-4 relative overflow-hidden shadow-sm hover:shadow-md transition-shadow ${isBest ? 'ring-1 ring-[#F59E0B]/50' : ''}`}
                          data-testid={`provider-card-${p.id}`}
                        >
                          {isBest && (
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#FDE047] to-[#F59E0B]"></div>
                          )}
                          <div className="flex flex-col">
                            {/* Card Body */}
                            <div className="p-4 sm:p-5 lg:p-6 flex flex-col 2xl:flex-row gap-5 items-stretch 2xl:items-center justify-between">
                              {/* Main Content (Avatar + Details) */}
                              <div className="flex-1 flex flex-col sm:flex-row items-start gap-4 sm:gap-5 min-w-0">
                                {/* Avatar */}
                                <div className="flex-shrink-0 relative">
                                  {p.profilePicture ? (
                                    <img src={`${API_BASE.replace('/api', '')}${p.profilePicture}`} alt={p.name} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover shadow-sm ring-4 ring-white dark:ring-slate-900" />
                                  ) : (
                                    <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-2xl sm:text-3xl font-medium shadow-sm ring-4 ring-white dark:ring-slate-900`}>
                                      {initials}
                                    </div>
                                  )}
                                  <div className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-emerald-500 border-2 border-white dark:border-slate-900 rounded-full"></div>
                                </div>

                                {/* Details */}
                                <div className="flex-1 min-w-0 mt-0.5">
                                  {isBest && (
                                    <div className="inline-flex items-center gap-1 text-[#D97706] bg-[#FEF3C7] text-[11px] font-bold px-2 py-0.5 rounded-full mb-2 uppercase tracking-wide">
                                      <CheckCircle className="w-3 h-3" /> Top Match
                                    </div>
                                  )}
                                  <div className="flex items-center justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                      <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white tracking-tight truncate">{p.name}</h3>
                                      <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400 mt-0.5">
                                        {p.practiceType?.toLowerCase() === 'group' || p.practiceType?.toLowerCase() === 'clinic' ? (
                                          <span className="font-semibold text-indigo-700 dark:text-indigo-300">Group Practice</span>
                                        ) : (
                                          <>{p.headline || p.specialty}{p.gender ? <span className="text-slate-400 font-normal"> · {p.gender}</span> : ""}</>
                                        )}
                                      </p>
                                    </div>
                                  </div>

                                  {/* Badges */}
                                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-2.5 sm:mt-3">
                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs font-semibold border border-amber-200/50 dark:border-amber-800/50">
                                      <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                                      <span>{(p.rating || 0).toFixed(2)} <span className="text-amber-600/70 dark:text-amber-500/70 font-medium">({p.reviewCount || 0})</span></span>
                                    </div>
                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium border border-slate-200 dark:border-slate-700">
                                      {p.practiceType?.toLowerCase() === 'clinic' || p.practiceType?.toLowerCase() === 'group' ? (
                                        <>
                                          <Building2 className="w-3.5 h-3.5 text-slate-400" />
                                          {p.practiceType?.toLowerCase() === 'group' ? 'Group Practice' : 'Clinic'}
                                        </>
                                      ) : (
                                        <>
                                          <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                                          {p.yearsExperience || 0} yrs exp
                                        </>
                                      )}
                                    </div>
                                    {p.certifications && p.certifications.length > 0 && (
                                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium border border-slate-200 dark:border-slate-700">
                                        <GraduationCap className="w-3.5 h-3.5 text-slate-400" />
                                        Board Certified
                                      </div>
                                    )}
                                    {p.isAssociated === false && (
                                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-xs font-semibold border border-amber-200 dark:border-amber-800">
                                        <HelpCircle className="w-3.5 h-3.5 text-amber-600" />
                                        Not Registered on Portal
                                      </div>
                                    )}
                                  </div>

                                  {/* Location & Time */}
                                  <div className="mt-3.5 sm:mt-4 space-y-2">
                                    <div className="flex items-start gap-2.5 text-[13px] text-slate-600 dark:text-slate-400">
                                      <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                      <span className="leading-snug">
                                        {p.hospital && <><span className="font-semibold text-slate-900 dark:text-slate-100">{p.hospital}</span><br /></>}
                                        <span className="text-slate-500 dark:text-slate-400">{addressString}</span>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Booking Widget (Action Buttons) */}
                              <div className="w-full 2xl:w-64 flex-shrink-0 pt-3 2xl:pt-0 border-t 2xl:border-t-0 border-slate-100 dark:border-slate-800/80">
                                <div className="bg-slate-50/80 dark:bg-slate-900/80 rounded-2xl p-3 sm:p-3.5 border border-slate-100 dark:border-slate-800">
                                  <div className="flex flex-col sm:flex-row 2xl:flex-col gap-2">
                                    {p.isAssociated === false || (p as any).provider?.isAssociated === false ? (
                                      <>
                                        <a href={`/provider/${(p.name || 'provider').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}/${p.id}`} className="flex-1 sm:w-auto 2xl:w-full" target="_blank" rel="noopener noreferrer">
                                          <Button
                                            className="w-full rounded-xl h-10 px-4 text-xs sm:text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-none shadow-sm hover:shadow-md transition-all duration-200"
                                          >
                                            View Profile
                                          </Button>
                                        </a>
                                        <Button
                                          className="flex-1 sm:w-auto 2xl:w-full rounded-xl h-10 px-4 text-xs sm:text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white border-none shadow-sm transition-all gap-2"
                                          onClick={() => setInviteProviderTarget(p)}
                                        >
                                          <Mail className="w-4 h-4" /> Tell Provider to Join
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <a href={`/provider/${(p.name || 'provider').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}/${p.id}`} className="flex-1 sm:w-auto 2xl:w-full" target="_blank" rel="noopener noreferrer">
                                          <Button
                                            className="w-full rounded-xl h-10 px-4 text-xs sm:text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-none shadow-sm hover:shadow-md transition-all duration-200"
                                          >
                                            View Profile
                                          </Button>
                                        </a>

                                        <Button
                                          variant="outline"
                                          onClick={() => setEstimatorProvider(selectedLinkedProvider || p)}
                                          className="flex-1 sm:w-auto 2xl:w-full rounded-xl h-10 px-4 text-xs sm:text-sm font-semibold border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-blue-700 hover:border-blue-200 transition-all duration-200 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                        >
                                          Good Faith Estimate
                                        </Button>

                                        <Button
                                          variant={compareList.some(c => c.id === p.id) ? "default" : "outline"}
                                          onClick={() => {
                                            setCompareList(prev => {
                                              if (prev.some(c => c.id === p.id)) return prev.filter(c => c.id !== p.id);
                                              if (prev.length >= 3) return prev; // max 3
                                              return [...prev, p];
                                            });
                                          }}
                                          className={`flex-1 sm:w-auto 2xl:w-full rounded-xl h-10 px-4 text-xs sm:text-sm font-semibold transition-all duration-200 ${compareList.some(c => c.id === p.id)
                                            ? "bg-emerald-500 hover:bg-emerald-600 text-white border-0 shadow-sm"
                                            : "border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-emerald-700 hover:border-emerald-200 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                            }`}
                                        >
                                          {compareList.some(c => c.id === p.id) ? (
                                            <><CheckCircle className="w-4 h-4 mr-2" /> Comparing</>
                                          ) : (
                                            <><Plus className="w-4 h-4 mr-2" /> Compare</>
                                          )}
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Providers List (Full Width at Bottom) */}
                            {p.linkedProviders && p.linkedProviders.length > 0 && (
                              <div className="border-t border-slate-200/60 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-800/20 p-3 sm:p-4">
                                <p className="text-[11px] font-bold text-slate-500 mb-2.5 uppercase tracking-wider flex items-center gap-2">
                                  <Stethoscope className="w-3.5 h-3.5" />
                                  Associated Providers
                                </p>
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                                  {p.linkedProviders.map((lp: any) => {
                                    const isSelected = lp.id === selectedLinkedProviderId;
                                    return (
                                      <div
                                        key={lp.id}
                                        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl border transition-colors cursor-pointer shadow-sm ${isSelected
                                          ? "bg-indigo-50/50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700 ring-1 ring-indigo-300 dark:ring-indigo-700"
                                          : "bg-white dark:bg-slate-800 border-slate-200/60 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-indigo-800"
                                          }`}
                                        onClick={() => setSelectedLinkedProviders(prev => ({ ...prev, [p.id]: lp.id }))}
                                        onDoubleClick={() => window.open(`/member/providers/${lp.name.toLowerCase().replace(/\s+/g, '-')}/${lp.id}`, '_blank')}
                                      >
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                          <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold text-xs ${isSelected
                                            ? "bg-indigo-100 dark:bg-indigo-800/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700"
                                            : "bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50"
                                            }`}>
                                            {lp.name.split(' ').map((n: string) => n[0]).slice(0, 2).join('')}
                                          </div>
                                          <div className="min-w-0 flex-1 pr-2">
                                            <div className="flex items-center gap-1.5">
                                              <p className={`text-sm font-bold leading-tight truncate ${isSelected ? "text-indigo-900 dark:text-indigo-100" : "text-slate-900 dark:text-white"}`}>{lp.name}</p>
                                              <a
                                                href={`/member/providers/${lp.name.toLowerCase().replace(/\s+/g, '-')}/${lp.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors shrink-0"
                                                title="View Provider Profile"
                                              >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                              </a>
                                            </div>
                                            <p className={`text-xs mt-0.5 truncate ${isSelected ? "text-indigo-600/80 dark:text-indigo-300/80" : "text-slate-500 dark:text-slate-400"}`}>
                                              {lp.headline || lp.specialty || 'General Practice'}
                                            </p>
                                            {lp.assignedLocations && lp.assignedLocations.length > 0 && (
                                              <div className="flex flex-wrap gap-1 mt-1.5">
                                                {lp.assignedLocations.map((locName: string, idx: number) => (
                                                  <span key={idx} className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800">
                                                    <MapPin className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                                                    <span className="truncate max-w-[150px]">{locName}</span>
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1 self-start sm:self-auto shrink-0 px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-bold border border-emerald-200/50 dark:border-emerald-800/50">
                                          <Shield className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                          <span>{lp.medicareCharge ? (lp.medicareCharge.includes('%') ? lp.medicareCharge : lp.medicareCharge + '%') : "80%"} Fee Schedule</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {/* Availability Calendar Full Width */}
                            <AnimatePresence>
                              {calOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden px-4 pb-4 md:px-5 md:pb-5 border-t border-slate-100 dark:border-slate-800"
                                >
                                  <AvailabilityCalendar providerId={p.id} providerName={p.name} />
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      );
                    })}

                    {/* Pagination */}
                    {!isLoading && totalEntries > 0 && (
                      <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-200/60 dark:border-slate-800 shrink-0 text-xs sm:text-[13px] text-slate-600 dark:text-slate-400">
                        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4">
                          <div className="flex items-center gap-2">
                            <span>Rows per page:</span>
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                              className="border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </select>
                          </div>
                          <span>
                            Showing {startIndex} to {endIndex} of {totalEntries} entries
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-1.5 rounded-md border border-slate-200 dark:border-slate-700 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors bg-white dark:bg-slate-900"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>

                          {getPageNumbers().map((p, index) => (
                            <button
                              key={index}
                              onClick={() => typeof p === 'number' && setCurrentPage(p)}
                              disabled={p === '...'}
                              className={`min-w-[32px] h-8 rounded-md flex items-center justify-center font-medium transition-colors ${p === currentPage
                                ? 'bg-blue-600 text-white border border-blue-600'
                                : p === '...'
                                  ? 'cursor-default text-slate-400'
                                  : 'bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700'
                                }`}
                            >
                              {p}
                            </button>
                          ))}

                          <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage >= totalPages}
                            className="p-1.5 rounded-md border border-slate-200 dark:border-slate-700 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors bg-white dark:bg-slate-900"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>{/* end right col */}
                </div>{/* end xl:grid */}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Dialog open={!!estimatorProvider} onOpenChange={(open) => {
        if (!open) {
          setEstimatorProvider(null);
          setSelectedCptCodes([]);
          setCptSearchQuery("");
        }
      }}>
        <DialogContent className="sm:max-w-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Good Faith Estimate (GFE)</DialogTitle>
            <DialogDescription>
              Select CPT codes to estimate the cost based on {estimatorProvider?.name}'s Fee Schedule percentage ({estimatorProvider?.medicareCharge ? estimatorProvider.medicareCharge : "100%"}).
            </DialogDescription>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search procedures by code or description..."
                value={cptSearchQuery}
                onChange={(e) => setCptSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
              />
            </div>
          </DialogHeader>

          <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-2 my-2">
            {masterCptCodes?.items?.filter((cpt: any) =>
              cpt.code.toLowerCase().includes(cptSearchQuery.toLowerCase()) ||
              cpt.description.toLowerCase().includes(cptSearchQuery.toLowerCase())
            ).map((cpt: any) => {
              const isSelected = selectedCptCodes.some(c => c.id === cpt.id);
              const medicareMultiplier = estimatorProvider?.medicareCharge ? parseFloat(estimatorProvider.medicareCharge.replace('%', '')) / 100 : 1;
              const u = cptQuantities[cpt.id] || 1;
              const unitPrice = cpt.standardPrice * (1 + medicareMultiplier);
              const payable = unitPrice * u;
              return (
                <div
                  key={cpt.id}
                  className={`p-3 rounded-lg border-2 transition-colors flex items-center justify-between ${isSelected ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
                >
                  <div
                    className="flex items-center gap-3 cursor-pointer flex-1 min-w-0"
                    onClick={() => {
                      if (isSelected) {
                        setSelectedCptCodes(prev => prev.filter(c => c.id !== cpt.id));
                      } else {
                        setSelectedCptCodes(prev => [...prev, cpt]);
                      }
                    }}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center border flex-shrink-0 ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 dark:border-slate-600'}`}>
                      {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-slate-900 dark:text-white truncate">{cpt.code} - {cpt.description}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {typeof cpt.category === 'object' && cpt.category !== null
                          ? ((cpt.category as any)?.name || (cpt.category as any)?.code || "")
                          : String(cpt.category || "")}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-2">
                    {isSelected && (
                      <div className="flex items-center gap-1 border border-slate-200 dark:border-slate-700 rounded-md px-1.5 py-0.5 bg-white dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] text-slate-500 font-semibold">Qty:</span>
                        <input
                          type="number"
                          min="1"
                          value={u}
                          onChange={(e) => {
                            const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                            setCptQuantities(prev => ({ ...prev, [cpt.id]: val }));
                          }}
                          className="w-8 text-center text-xs font-bold bg-transparent focus:outline-none"
                        />
                      </div>
                    )}
                    <div className="text-right min-w-[60px]">
                      <div className="text-sm font-bold text-slate-900 dark:text-white">${payable.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 mt-2">
            <div>
              <div className="text-xs sm:text-sm text-slate-500 font-medium">Estimated Payable Amount</div>
              <div className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
                ${selectedCptCodes.reduce((sum, cpt) => {
                  const medicareMultiplier = estimatorProvider?.medicareCharge ? parseFloat(estimatorProvider.medicareCharge.replace('%', '')) / 100 : 1;
                  const u = cptQuantities[cpt.id] || 1;
                  return sum + (cpt.standardPrice * (1 + medicareMultiplier) * u);
                }, 0).toFixed(2)}
              </div>
            </div>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 h-10 sm:h-11 font-semibold text-sm"
              disabled={selectedCptCodes.length === 0 || createEstimate.isPending}
              onClick={() => {
                const medicareMultiplier = estimatorProvider?.medicareCharge ? parseFloat(estimatorProvider.medicareCharge.replace('%', '')) / 100 : 1;
                const totalCost = selectedCptCodes.reduce((sum, cpt) => {
                  const u = cptQuantities[cpt.id] || 1;
                  return sum + (cpt.standardPrice * (1 + medicareMultiplier) * u);
                }, 0);

                createEstimate.mutate({
                  data: {
                    procedure: "Custom Estimate",
                    cptCodes: selectedCptCodes.map(c => `${c.code}${(cptQuantities[c.id] || 1) > 1 ? ` (${cptQuantities[c.id]} units)` : ''}`).join(", "),
                    providerName: estimatorProvider?.name,
                    providerType: estimatorProvider?.headline || estimatorProvider?.specialty || "Specialist",
                    providerId: estimatorProvider?.id,
                    patientResponsibility: totalCost,
                    estimatedCost: totalCost,
                    insurancePlan: "StraightDoc"
                  }
                }, {
                  onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: getListCostEstimatesQueryKey() });
                    toast({
                      title: "Estimate Saved",
                      description: "Your cost estimate has been saved successfully.",
                    });
                    setEstimatorProvider(null);
                    setSelectedCptCodes([]);
                    setCptQuantities({});
                  }
                });
              }}
            >
              {createEstimate.isPending ? "Saving..." : "Save Estimate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sticky Compare Action Bar */}
      {compareList.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 p-3 sm:p-4 z-40 transform transition-all duration-300 translate-y-0 shadow-lg">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-3">
                {compareList.map((p, i) => (
                  <div key={p.id} className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold border-2 border-white dark:border-slate-900 shadow-sm relative z-[3] group">
                    {p.name.split(" ").map((n: string) => n[0]).slice(0, 2).join("")}
                    <button
                      onClick={(e) => { e.stopPropagation(); setCompareList(prev => prev.filter(c => c.id !== p.id)); }}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <span className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300">
                {compareList.length} provider{compareList.length > 1 ? 's' : ''} selected
              </span>
              {compareList.length === 3 && <span className="text-[10px] sm:text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full ml-1 sm:ml-2 font-medium">Max reached</span>}
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <Button variant="ghost" size="sm" onClick={() => setCompareList([])} className="text-slate-500 text-xs sm:text-sm">
                Clear
              </Button>
              <Button
                onClick={() => setShowCompareModal(true)}
                disabled={compareList.length < 2}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-600/20 h-9 sm:h-10 px-5 sm:px-6 font-bold text-xs sm:text-sm"
              >
                Compare Providers
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Comparison Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-500" /> Compare Providers
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setShowCompareModal(false)} className="rounded-full">
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-0 overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[600px]">
                <thead className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md z-10 shadow-sm">
                  <tr>
                    <th className="p-6 bg-slate-50/70 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 font-medium text-slate-500 w-1/4">
                      {/* Empty top-left cell */}
                    </th>
                    {compareList.map(p => (
                      <th key={p.id} className="p-6 bg-slate-50/70 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 text-center relative group w-1/4 align-top">
                        <button
                          onClick={() => setCompareList(prev => prev.filter(c => c.id !== p.id))}
                          className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 text-white flex items-center justify-center font-black text-xl mb-3 shadow-sm border-2 border-white dark:border-slate-800">
                            {p.name.split(" ").map((n: string) => n[0]).slice(0, 2).join("")}
                          </div>
                          <div className="font-bold text-slate-900 dark:text-white text-base leading-tight mb-1">{p.name}</div>
                          <div className="text-xs font-semibold text-slate-500 tracking-wide uppercase">{p.headline || p.specialty}</div>
                        </div>
                      </th>
                    ))}
                    {/* Add empty columns to maintain width if comparing fewer than 3 */}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => (
                      <th key={`empty-th-${i}`} className="p-6 bg-slate-50/50 dark:bg-slate-900/20 border-b border-slate-100 dark:border-slate-800 w-1/4"></th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:border-slate-800">
                  <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                    <td className="p-6 font-semibold text-slate-600 dark:text-slate-400">% of Fee Schedule</td>
                    {compareList.map(p => (
                      <td key={p.id} className="p-6 text-center font-bold text-emerald-600 dark:text-emerald-400 text-lg">
                        {p.medicareCharge ? (p.medicareCharge.includes('%') ? p.medicareCharge : p.medicareCharge + '%') : "80%"}
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => <td key={`empty-medicare-top-${i}`}></td>)}
                  </tr>
                  <tr className="bg-slate-50/30 dark:bg-slate-900/10 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="p-6 font-semibold text-slate-600 dark:text-slate-400">Experience</td>
                    {compareList.map(p => (
                      <td key={p.id} className="p-6 text-center font-medium text-slate-700 dark:text-slate-300">
                        {p.yearsExperience} Years
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => <td key={`empty-exp-${i}`}></td>)}
                  </tr>
                  <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                    <td className="p-6 font-semibold text-slate-600 dark:text-slate-400">Rating</td>
                    {compareList.map(p => (
                      <td key={p.id} className="p-6 text-center font-bold text-slate-900 dark:text-white">
                        <div className="flex items-center justify-center gap-1.5">
                          <Star className="w-5 h-5 text-amber-400 fill-amber-400" /> {p.rating}
                        </div>
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => <td key={`empty-rating-${i}`}></td>)}
                  </tr>
                  <tr className="bg-slate-50/30 dark:bg-slate-900/10 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="p-6 font-semibold text-slate-600 dark:text-slate-400">Telehealth</td>
                    {compareList.map(p => (
                      <td key={p.id} className="p-6 text-center">
                        {p.telehealth ?
                          <span className="inline-flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 py-1.5 px-3 rounded-full text-xs font-bold gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Yes</span> :
                          <span className="inline-flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 py-1.5 px-3 rounded-full text-xs font-bold gap-1.5"><X className="w-3.5 h-3.5" /> No</span>
                        }
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => <td key={`empty-tele-${i}`}></td>)}
                  </tr>
                  <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                    <td className="p-6 font-semibold text-slate-600 dark:text-slate-400">Languages</td>
                    {compareList.map(p => (
                      <td key={p.id} className="p-6 text-center text-sm font-medium text-slate-700 dark:text-slate-300">
                        {p.languages?.length > 0 ? p.languages.map((l: any) => l.name).join(", ") : "English, Spanish"}
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => <td key={`empty-lang-${i}`}></td>)}
                  </tr>

                  <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                    <td className="p-6 font-semibold text-slate-600 dark:text-slate-400">Education</td>
                    {compareList.map(p => (
                      <td key={p.id} className="p-6 text-center text-sm font-medium text-slate-700 dark:text-slate-300">
                        {p.education || "MD - Harvard Medical School"}
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => <td key={`empty-edu-${i}`}></td>)}
                  </tr>
                  <tr className="bg-slate-50/30 dark:bg-slate-900/10 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="p-6 font-semibold text-slate-600 dark:text-slate-400">Works</td>
                    {compareList.map(p => (
                      <td key={p.id} className="p-6 text-center text-sm font-medium text-slate-700 dark:text-slate-300">
                        {p.works || "General Hospital, City Clinic"}
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => <td key={`empty-works-${i}`}></td>)}
                  </tr>
                  <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                    <td className="p-6 font-semibold text-slate-600 dark:text-slate-400">Achievements</td>
                    {compareList.map(p => (
                      <td key={p.id} className="p-6 text-center text-sm font-medium text-slate-700 dark:text-slate-300">
                        {p.achievements || "Top Provider 2023, Research Grant"}
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => <td key={`empty-achieve-${i}`}></td>)}
                  </tr>
                  <tr className="bg-slate-50/30 dark:bg-slate-900/10 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="p-6 font-semibold text-slate-600 dark:text-slate-400">Pre-defined Bundles</td>
                    {compareList.map(p => (
                      <td key={p.id} className="p-6 text-center">
                        <span className="inline-flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 py-1 px-3 rounded-full text-xs font-semibold">
                          {p.bundles?.length || 3} Bundles Available
                        </span>
                      </td>
                    ))}
                    {Array.from({ length: Math.max(0, 3 - compareList.length) }).map((_, i) => <td key={`empty-bundles-${i}`}></td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Invite Provider Modal */}
      <Dialog open={!!inviteProviderTarget} onOpenChange={(open) => !open && setInviteProviderTarget(null)}>
        <DialogContent className="sm:max-w-lg rounded-3xl p-6 overflow-hidden">
          <DialogHeader className="text-left">
            <div className="w-12 h-12 bg-amber-50 dark:bg-amber-950/60 rounded-2xl flex items-center justify-center mb-3 text-amber-600 dark:text-amber-400">
              <Stethoscope className="w-6 h-6" />
            </div>
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
              Invite {inviteProviderTarget?.name || 'Provider'} to Join StraightDoc
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
              {inviteProviderTarget?.name || 'This provider'} is currently listed in our healthcare directory but has not yet joined the StraightDoc portal.
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 my-2 rounded-2xl bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800 text-amber-900 dark:text-amber-200 text-xs leading-relaxed font-medium">
            "Tell your provider you want transparent pricing and seamless direct appointments with them on StraightDoc. We will send an invitation on your behalf to help onboard them to our network!"
          </div>

          <form onSubmit={handleSendInvitation} className="space-y-4 mt-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">Your Name (Optional)</label>
              <Input
                placeholder="e.g. Jane Doe"
                value={invitePatientName}
                onChange={(e) => setInvitePatientName(e.target.value)}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">Message / Note to Provider (Optional)</label>
              <Input
                placeholder="I would love to book direct appointments with your practice on StraightDoc!"
                value={inviteNote}
                onChange={(e) => setInviteNote(e.target.value)}
                className="rounded-xl"
              />
            </div>

            <DialogFooter className="sm:justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" className="rounded-xl" onClick={() => setInviteProviderTarget(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold gap-2"
                disabled={inviteProviderMutation.isPending}
              >
                {inviteProviderMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending Invitation...</>
                ) : (
                  <><Mail className="w-4 h-4" /> Send Invitation</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </MemberLayout>
  );
}
